import { useTranslations } from 'next-intl';
import { FC, KeyboardEvent, useCallback, useState } from 'react';
import { toast } from 'sonner';
import type { ActiveOrder } from '@/lib/api/history/models';
import { useUpdateLimitOrder } from '@/lib/api/order/hooks';
import { ExpirationEdit } from '@/lib/components/ExpirationEdit';
import { Badge } from '../ui-kit/badge';
import { Button } from '../ui-kit/button';
import { Icon } from '../ui-kit/icons';
import { Input } from '../ui-kit/input';
import { Spinner } from '../ui-kit/spinner';
import { Text } from '../ui-kit/text';
import { cn } from '../ui-kit/utils';

interface EventOpenOrdersCardProps {
  order: {
    id: string;
    candidate: string;
    outcome: 'Yes' | 'No';
    side: 'Buy' | 'Sell';
    price: string; // 58¢
    filled: string;
    request: string;
    orderValue: string; // $100
    expiration: string; // "24 April 2025, 15:45 UTC" or "-"
    originalOrder: ActiveOrder;
  };
  eventId: string;
  outcomeId: string;
  resolutions?: Array<{ id: string; name: string; isPositive: boolean }>;
  quoteAssetId: string;
  yesSharesBalance?: number;
  noSharesBalance?: number;
  onCancel: () => void;
}

export const EventOpenOrdersCard: FC<EventOpenOrdersCardProps> = ({
  order,
  eventId,
  outcomeId,
  resolutions,
  quoteAssetId,
  yesSharesBalance,
  noSharesBalance,
  onCancel,
}) => {
  const updateLimitOrderMutation = useUpdateLimitOrder();
  const t = useTranslations('event.openOrders');
  const tToasts = useTranslations('toasts.orders');

  // Inline edit state for Price column
  const [editingPrice, setEditingPrice] = useState(false);
  const [tempPrice, setTempPrice] = useState('');

  // Inline edit state for Request column
  const [editingRequest, setEditingRequest] = useState(false);
  const [tempRequest, setTempRequest] = useState('');

  // Price edit handlers
  const startPriceEdit = useCallback(() => {
    setEditingPrice(true);
    const numeric = order.price.replace(/[^0-9.]/g, '');
    setTempPrice(numeric);
  }, [order.price]);

  const cancelPriceEdit = useCallback(() => {
    setEditingPrice(false);
    setTempPrice('');
  }, []);

  const savePriceEdit = useCallback(async () => {
    const newPriceValue = parseFloat(tempPrice);
    if (isNaN(newPriceValue)) {
      toast.error(tToasts('invalidPrice'));
      return;
    }

    if (newPriceValue < 0 || newPriceValue > 99) {
      toast.error(tToasts('priceRange'));
      return;
    }

    const currentPriceInCents = Math.round(order.originalOrder.price * 100);
    if (newPriceValue === currentPriceInCents) {
      cancelPriceEdit();
      return;
    }

    try {
      const resolution = resolutions?.find((r) => r.id === order.originalOrder.assetId);
      if (!resolution) {
        toast.error(tToasts('resolutionNotFound'));
        cancelPriceEdit();
        return;
      }

      if (!quoteAssetId) {
        toast.error(tToasts('usdcNotFound'));
        cancelPriceEdit();
        return;
      }

      const request = order.originalOrder.originalQuantity;
      const filled = order.originalOrder.quantity;
      const quantityDifference = request - filled;

      await updateLimitOrderMutation.mutateAsync({
        orderId: order.id,
        body: {
          eventId,
          eventOutcomeId: outcomeId,
          baseAssetId: resolution.id,
          quoteAssetId,
          side: order.originalOrder.side,
          price: newPriceValue / 100,
          quantity: quantityDifference,
        },
      });

      toast.success(tToasts('priceUpdated'));
      cancelPriceEdit();
    } catch (error) {
      console.error('Failed to update order price:', error);
      toast.error(tToasts('priceUpdateFailed'));
    }
  }, [
    tempPrice,
    order,
    eventId,
    outcomeId,
    resolutions,
    quoteAssetId,
    updateLimitOrderMutation,
    cancelPriceEdit,
    tToasts,
  ]);

  const onPriceKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        savePriceEdit();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelPriceEdit();
      }
    },
    [savePriceEdit, cancelPriceEdit],
  );

  // Request edit handlers
  const startRequestEdit = useCallback(() => {
    setEditingRequest(true);
    setTempRequest(order.request);
  }, [order.request]);

  const cancelRequestEdit = useCallback(() => {
    setEditingRequest(false);
    setTempRequest('');
  }, []);

  const saveRequestEdit = useCallback(async () => {
    const newQuantity = parseInt(tempRequest, 10);
    if (isNaN(newQuantity) || newQuantity <= 0) {
      toast.error(tToasts('invalidQuantity'));
      return;
    }

    const currentQuantity = order.originalOrder.originalQuantity;
    if (newQuantity === currentQuantity) {
      cancelRequestEdit();
      return;
    }

    if (order.originalOrder.side === 'SELL') {
      const sharesBalance = order.outcome === 'Yes' ? yesSharesBalance : noSharesBalance;
      const currentUnfilled = order.originalOrder.originalQuantity - order.originalOrder.quantity;
      const availableShares = (sharesBalance ?? 0) + currentUnfilled;

      if (sharesBalance !== undefined && newQuantity > availableShares) {
        toast.error(tToasts('quantityExceedsBalance', { shares: availableShares }));
        return;
      }
    }

    try {
      const resolution = resolutions?.find((r) => r.id === order.originalOrder.assetId);
      if (!resolution) {
        toast.error(tToasts('resolutionNotFound'));
        cancelRequestEdit();
        return;
      }

      if (!quoteAssetId) {
        toast.error(tToasts('usdcNotFound'));
        cancelRequestEdit();
        return;
      }

      await updateLimitOrderMutation.mutateAsync({
        orderId: order.id,
        body: {
          eventId,
          eventOutcomeId: outcomeId,
          baseAssetId: resolution.id,
          quoteAssetId,
          side: order.originalOrder.side,
          price: order.originalOrder.price,
          quantity: newQuantity,
        },
      });

      toast.success(tToasts('quantityUpdated'));
      cancelRequestEdit();
    } catch (error) {
      console.error('Failed to update order quantity:', error);
      toast.error(tToasts('quantityUpdateFailed'));
    }
  }, [
    tempRequest,
    order,
    eventId,
    outcomeId,
    resolutions,
    quoteAssetId,
    yesSharesBalance,
    noSharesBalance,
    updateLimitOrderMutation,
    cancelRequestEdit,
    tToasts,
  ]);

  const onRequestKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        saveRequestEdit();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelRequestEdit();
      }
    },
    [saveRequestEdit, cancelRequestEdit],
  );

  // Handler for updating expiration
  const handleSaveExpiration = useCallback(
    async (isoDate: string) => {
      try {
        const resolution = resolutions?.find((r) => r.id === order.originalOrder.assetId);
        if (!resolution) {
          toast.error(tToasts('resolutionNotFound'));
          throw new Error('Failed to find resolution');
        }

        if (!quoteAssetId) {
          toast.error(tToasts('usdcNotFound'));
          throw new Error('USDC asset ID not found');
        }

        const request = order.originalOrder.originalQuantity;
        const filled = order.originalOrder.quantity;
        const quantityDifference = request - filled;

        await updateLimitOrderMutation.mutateAsync({
          orderId: order.id,
          body: {
            eventId,
            eventOutcomeId: outcomeId,
            baseAssetId: resolution.id,
            quoteAssetId,
            side: order.originalOrder.side,
            price: order.originalOrder.price,
            quantity: quantityDifference,
            expireAt: isoDate,
          },
        });

        toast.success(tToasts('expirationUpdated'));
      } catch (error) {
        console.error('Failed to update expiration:', error);
        toast.error(tToasts('expirationUpdateFailed'));
        throw error;
      }
    },
    [order, resolutions, quoteAssetId, eventId, outcomeId, updateLimitOrderMutation, tToasts],
  );

  // Handler for deleting expiration
  const handleDeleteExpiration = useCallback(async () => {
    try {
      const resolution = resolutions?.find((r) => r.id === order.originalOrder.assetId);
      if (!resolution) {
        toast.error(tToasts('resolutionNotFound'));
        throw new Error('Failed to find resolution');
      }

      if (!quoteAssetId) {
        toast.error(tToasts('usdcNotFound'));
        throw new Error('USDC asset ID not found');
      }

      const request = order.originalOrder.originalQuantity;
      const filled = order.originalOrder.quantity;
      const quantityDifference = request - filled;

      await updateLimitOrderMutation.mutateAsync({
        orderId: order.id,
        body: {
          eventId,
          eventOutcomeId: outcomeId,
          baseAssetId: resolution.id,
          quoteAssetId,
          side: order.originalOrder.side,
          price: order.originalOrder.price,
          quantity: quantityDifference,
          expireAt: null,
        },
      });

      toast.success(tToasts('expirationDeleted'));
    } catch (error) {
      console.error('Failed to delete expiration:', error);
      toast.error(tToasts('expirationDeleteFailed'));
      throw error;
    }
  }, [order, resolutions, quoteAssetId, eventId, outcomeId, updateLimitOrderMutation, tToasts]);

  return (
    <>
      <div className="bg-v2-surface-secondary rounded-[10px] p-4">
        {/* Desktop layout (>768px) */}
        <div className="hidden items-center gap-4 md:flex">
          <Badge
            className={cn(
              'label-lg w-[80px] rounded-[10px] px-2 py-1',
              order.outcome === 'No' ? 'text-red-500' : 'text-success',
            )}
            style={{
              backgroundColor:
                order.outcome === 'No' ? 'rgba(225, 55, 54, 0.20)' : 'rgba(18, 155, 68, 0.20)',
            }}
          >
            {order.outcome}
          </Badge>
          <div className="flex w-[140px] flex-col gap-1">
            <Text variant="body-xs" className="text-v2-text-secondary">
              {t('price')}
            </Text>
            {!editingPrice ? (
              <div className="flex items-center gap-2">
                <Text variant="body-small">{order.price}</Text>
                <button
                  type="button"
                  className="border-1 border-v2-border hover:bg-v2-btn-secondary-hover flex cursor-pointer items-center justify-center rounded-[4px]
                    px-1 py-1"
                  onClick={startPriceEdit}
                >
                  <Icon name="Edit" size={12} className="text-v2-text-primary" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  className="rounded-[4px] !px-1"
                  onClick={cancelPriceEdit}
                >
                  <Icon name="Close" size={16} />
                </Button>
                <Input
                  value={tempPrice}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^0-9.]/g, '');
                    const num = parseFloat(cleaned);
                    if (!isNaN(num) && num > 99) {
                      setTempPrice('99');
                    } else {
                      setTempPrice(cleaned);
                    }
                  }}
                  onKeyDown={onPriceKeyDown}
                  autoFocus
                  inputMode="decimal"
                  className="border-v2-border border-1 bg-v2-btn-secondary-hover !h-6 w-[72px] rounded-[4px] px-2 text-center"
                />
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  className="rounded-[4px] !px-1"
                  onClick={savePriceEdit}
                  disabled={updateLimitOrderMutation.isPending}
                >
                  {updateLimitOrderMutation.isPending ? (
                    <Spinner size={16} />
                  ) : (
                    <Icon name="Check" size={16} />
                  )}
                </Button>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="body-xs" className="text-v2-text-secondary">
              {t('value')}
            </Text>
            <Text variant="body-small">{order.orderValue}</Text>
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="body-xs" className="text-v2-text-secondary">
              {t('filledRequest')}
            </Text>
            {!editingRequest ? (
              <div className="flex items-center gap-2">
                <Text variant="body-small">
                  {order.filled}/{order.request}
                </Text>
                <button
                  type="button"
                  className="border-1 border-v2-border hover:bg-v2-btn-secondary-hover flex cursor-pointer items-center justify-center rounded-[4px]
                    px-1 py-1"
                  onClick={startRequestEdit}
                >
                  <Icon name="Edit" size={12} className="text-v2-text-primary" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  className="rounded-[4px] !px-1"
                  onClick={cancelRequestEdit}
                >
                  <Icon name="Close" size={16} />
                </Button>
                <Input
                  value={tempRequest}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setTempRequest(value.replace(/^0+(\d)/, '$1'));
                  }}
                  onKeyDown={onRequestKeyDown}
                  autoFocus
                  inputMode="numeric"
                  className="border-v2-border border-1 bg-v2-btn-secondary-hover !h-6 w-[72px] rounded-[4px] px-2 text-center"
                />
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  className="rounded-[4px] !px-1"
                  onClick={saveRequestEdit}
                  disabled={updateLimitOrderMutation.isPending}
                >
                  {updateLimitOrderMutation.isPending ? (
                    <Spinner size={16} />
                  ) : (
                    <Icon name="Check" size={16} />
                  )}
                </Button>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <Text variant="body-xs" className="text-v2-text-secondary">
              {t('expiration')}
            </Text>
            <ExpirationEdit
              value={order.expiration}
              onSave={handleSaveExpiration}
              onDelete={handleDeleteExpiration}
              className="text-center"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              className="body-small !px-2 !py-1"
              onClick={onCancel}
            >
              {t('cancel')}
            </Button>
          </div>
        </div>

        {/* Tablet layout (560-768px) */}
        <div
          className="hidden
            grid-cols-[minmax(50px,60px)_minmax(70px,1fr)_minmax(60px,1fr)_minmax(70px,1fr)_minmax(80px,1fr)_auto]
            items-center gap-2 min-[560px]:grid md:hidden"
        >
          <Badge
            className={cn(
              'label-lg w-fit rounded-[10px] px-2 py-1',
              order.outcome === 'No' ? 'text-red-500' : 'text-success',
            )}
            style={{
              backgroundColor:
                order.outcome === 'No' ? 'rgba(225, 55, 54, 0.20)' : 'rgba(18, 155, 68, 0.20)',
            }}
          >
            {order.outcome}
          </Badge>
          <div className="flex min-w-0 flex-col gap-1">
            <Text variant="body-xs" className="text-v2-text-secondary">
              {t('price')}
            </Text>
            {!editingPrice ? (
              <div className="flex items-center gap-1">
                <Text variant="body-small" className="truncate">
                  {order.price}
                </Text>
                <button
                  type="button"
                  className="border-1 border-v2-border hover:bg-v2-btn-secondary-hover flex shrink-0 cursor-pointer items-center justify-center
                    rounded-[4px] px-1 py-1"
                  onClick={startPriceEdit}
                >
                  <Icon name="Edit" size={12} className="text-v2-text-primary" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  className="shrink-0 rounded-[4px] !px-1"
                  onClick={cancelPriceEdit}
                >
                  <Icon name="Close" size={16} />
                </Button>
                <Input
                  value={tempPrice}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^0-9.]/g, '');
                    const num = parseFloat(cleaned);
                    if (!isNaN(num) && num > 99) {
                      setTempPrice('99');
                    } else {
                      setTempPrice(cleaned);
                    }
                  }}
                  onKeyDown={onPriceKeyDown}
                  autoFocus
                  inputMode="decimal"
                  className="border-v2-border border-1 bg-v2-btn-secondary-hover !h-6 w-[60px] rounded-[4px] px-1 text-center"
                />
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  className="shrink-0 rounded-[4px] !px-1"
                  onClick={savePriceEdit}
                  disabled={updateLimitOrderMutation.isPending}
                >
                  {updateLimitOrderMutation.isPending ? (
                    <Spinner size={16} />
                  ) : (
                    <Icon name="Check" size={16} />
                  )}
                </Button>
              </div>
            )}
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <Text variant="body-xs" className="text-v2-text-secondary">
              {t('value')}
            </Text>
            <Text variant="body-small" className="truncate">
              {order.orderValue}
            </Text>
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <Text variant="body-xs" className="text-v2-text-secondary">
              {t('filledRequest')}
            </Text>
            {!editingRequest ? (
              <div className="flex items-center gap-1">
                <Text variant="body-small" className="truncate">
                  {order.filled}/{order.request}
                </Text>
                <button
                  type="button"
                  className="border-1 border-v2-border hover:bg-v2-btn-secondary-hover flex shrink-0 cursor-pointer items-center justify-center
                    rounded-[4px] px-1 py-1"
                  onClick={startRequestEdit}
                >
                  <Icon name="Edit" size={12} className="text-v2-text-primary" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  className="shrink-0 rounded-[4px] !px-1"
                  onClick={cancelRequestEdit}
                >
                  <Icon name="Close" size={16} />
                </Button>
                <Input
                  value={tempRequest}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setTempRequest(value.replace(/^0+(\d)/, '$1'));
                  }}
                  onKeyDown={onRequestKeyDown}
                  autoFocus
                  inputMode="numeric"
                  className="border-v2-border border-1 bg-v2-btn-secondary-hover !h-6 w-[60px] rounded-[4px] px-1 text-center"
                />
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  className="shrink-0 rounded-[4px] !px-1"
                  onClick={saveRequestEdit}
                  disabled={updateLimitOrderMutation.isPending}
                >
                  {updateLimitOrderMutation.isPending ? (
                    <Spinner size={16} />
                  ) : (
                    <Icon name="Check" size={16} />
                  )}
                </Button>
              </div>
            )}
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <Text variant="body-xs" className="text-v2-text-secondary truncate">
              {t('expiration')}
            </Text>
            <ExpirationEdit
              value={order.expiration}
              onSave={handleSaveExpiration}
              onDelete={handleDeleteExpiration}
              className="text-left"
            />
          </div>
          <div className="flex shrink-0 items-center">
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              className="body-small rounded-[4px] !px-2 py-1"
              onClick={onCancel}
            >
              {t('cancel')}
            </Button>
          </div>
        </div>

        {/* Mobile layout (≤559px) */}
        <div className="flex flex-col gap-4 min-[560px]:hidden">
          {/* Badge */}
          <div className="flex items-center gap-2">
            <Badge
              className={cn(
                'label-lg rounded-[10px] px-2 py-1',
                order.outcome === 'No' ? 'text-red-500' : 'text-success',
              )}
              style={{
                backgroundColor:
                  order.outcome === 'No' ? 'rgba(225, 55, 54, 0.20)' : 'rgba(18, 155, 68, 0.20)',
              }}
            >
              {order.outcome}
            </Badge>
          </div>

          {/* Grid fields */}
          <div className="grid grid-cols-2 gap-4">
            {/* Price */}
            <div className="flex flex-col gap-1">
              <Text variant="body-small" className="text-v2-text-secondary">
                {t('price')}
              </Text>
              {!editingPrice ? (
                <div className="flex items-center gap-2">
                  <Text variant="body-small" className="text-v2-text-primary">
                    {order.price}
                  </Text>
                  <button
                    type="button"
                    className="border-1 border-v2-border hover:bg-v2-btn-secondary-hover flex cursor-pointer items-center justify-center rounded-[4px]
                      px-1 py-1"
                    onClick={startPriceEdit}
                  >
                    <Icon name="Edit" size={12} className="text-v2-text-primary" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outlined"
                    color="primary"
                    size="sm"
                    className="rounded-[4px] !px-1"
                    onClick={cancelPriceEdit}
                  >
                    <Icon name="Close" size={16} />
                  </Button>
                  <Input
                    value={tempPrice}
                    onChange={(e) => {
                      const cleaned = e.target.value.replace(/[^0-9.]/g, '');
                      const num = parseFloat(cleaned);
                      if (!isNaN(num) && num > 99) {
                        setTempPrice('99');
                      } else {
                        setTempPrice(cleaned);
                      }
                    }}
                    onKeyDown={onPriceKeyDown}
                    autoFocus
                    inputMode="decimal"
                    className="border-v2-border border-1 bg-v2-btn-secondary-hover !h-6 w-[72px] rounded-[4px] px-2 text-center"
                  />
                  <Button
                    variant="outlined"
                    color="primary"
                    size="sm"
                    className="rounded-[4px] !px-1"
                    onClick={savePriceEdit}
                    disabled={updateLimitOrderMutation.isPending}
                  >
                    {updateLimitOrderMutation.isPending ? (
                      <Spinner size={16} />
                    ) : (
                      <Icon name="Check" size={16} />
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Value */}
            <div className="flex flex-col gap-1">
              <Text variant="body-small" className="text-v2-text-secondary">
                {t('value')}
              </Text>
              <Text variant="body-small" className="text-v2-text-primary">
                {order.orderValue}
              </Text>
            </div>

            {/* Filled / Request */}
            <div className="flex flex-col gap-1">
              <Text variant="body-small" className="text-v2-text-secondary">
                {t('filledRequest')}
              </Text>
              {!editingRequest ? (
                <div className="flex items-center gap-2">
                  <Text variant="body-small" className="text-v2-text-primary">
                    {order.filled}/{order.request}
                  </Text>
                  <button
                    type="button"
                    className="border-1 border-v2-border hover:bg-v2-btn-secondary-hover flex cursor-pointer items-center justify-center rounded-[4px]
                      px-1 py-1"
                    onClick={startRequestEdit}
                  >
                    <Icon name="Edit" size={12} className="text-v2-text-primary" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outlined"
                    color="primary"
                    size="sm"
                    className="rounded-[4px] !px-1"
                    onClick={cancelRequestEdit}
                  >
                    <Icon name="Close" size={16} />
                  </Button>
                  <Input
                    value={tempRequest}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setTempRequest(value.replace(/^0+(\d)/, '$1'));
                    }}
                    onKeyDown={onRequestKeyDown}
                    autoFocus
                    inputMode="numeric"
                    className="border-v2-border border-1 bg-v2-btn-secondary-hover !h-6 w-[72px] rounded-[4px] px-2 text-center"
                  />
                  <Button
                    variant="outlined"
                    color="primary"
                    size="sm"
                    className="rounded-[4px] !px-1"
                    onClick={saveRequestEdit}
                    disabled={updateLimitOrderMutation.isPending}
                  >
                    {updateLimitOrderMutation.isPending ? (
                      <Spinner size={16} />
                    ) : (
                      <Icon name="Check" size={16} />
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Expiration */}
            <div className="flex flex-col gap-1">
              <Text variant="body-small" className="text-v2-text-secondary">
                {t('expiration')}
              </Text>
              <ExpirationEdit
                value={order.expiration}
                onSave={handleSaveExpiration}
                onDelete={handleDeleteExpiration}
              />
            </div>
          </div>

          {/* Action button */}
          <Button
            variant="outlined"
            color="primary"
            size="md"
            className="body-small w-full !py-2"
            onClick={onCancel}
          >
            {t('cancel')}
          </Button>
        </div>
      </div>
    </>
  );
};
